import { Plus } from "lucide-react";
import { Fragment } from "react";
import { observer } from "mobx-react-lite";
import { IHeroSliderBlockData } from "../../types/content-block-data";
import SlideDropZone from "./shared/hero-slider/slide-drop-zone";
import CarouselSlidePreview from "./shared/hero-slider/hero-slide-preview";
import contentManager from "../../stores/content-manager";
import blocksManager from "../../stores/blocks-manager";
import pageBuilderStore from "../../stores/pageBuilderStore";
import { getDefaultHeroSlide } from "../../utils/blocks-factory";

interface IHeroSliderBottomBarProps {
  blockId: string;
}

/**
 * HeroSliderBottomPanel – Slide Management Panel for Hero Slider Blocks
 *
 * @component
 * @description
 * Displays an interactive, fixed-position panel at the bottom of the Design view
 * for managing slides in a HeroSlider block. Allows users to preview, select,
 * reorder (drag-and-drop), and add new slides to their hero carousel.
 *
 * @features
 * - Shows a count of total slides
 * - Renders previews for all slides (click to select, highlight active)
 * - Allows drag-and-drop reordering of slides via SlideDropZone
 * - Provides a button to add new slides with sensible defaults
 * - Highlights the currently selected slide for editing
 * - Integrates tightly with MobX stores for block data, selection, and updates
 *
 * @state
 * - Uses contentManager for reading/updating HeroSlider block data
 * - Uses blocksManager to track and set the currently selected slide
 * - Uses pageBuilderStore to set the active panel when a slide is selected
 *
 * @props
 * @param {string} blockId – The unique identifier for the HeroSlider block being managed.
 *
 * @remarks
 * - Only visible when a HeroSlider block is selected in the Design view
 * - Not designed for reuse outside the PageBuilder's hero slider editing context
 * - Styling and positioning assume fixed bottom placement over the canvas area
 *
 * @example
 * // Used within DesignCanvas when editing a HeroSlider block
 * <HeroSliderBottomPanel blockId="hero-slider-123" />
 *
 * @since 2025-06-18
 * @lastUpdated 2025-07-15
 * @author
 * d2d-weizhi (Chen Weizhi)
 */
export default observer(function HeroSliderBottomPanel({
  blockId,
}: IHeroSliderBottomBarProps) {


  const block = contentManager.getContentBlockById(blockId);
  if (!block || block.blockType !== "hero_slider") return null;
  const heroSliderData = block.blockData as IHeroSliderBlockData;

  function reorderSlides(fromIdx: number, toIdx: number) {
    if (fromIdx === toIdx) return;
    const newSlides = [...heroSliderData.slides];
    const [moved] = newSlides.splice(fromIdx, 1);
    newSlides.splice(toIdx > fromIdx ? toIdx - 1 : toIdx, 0, moved);
    contentManager.setHeroSliderConfig(blockId, { slides: newSlides });
    // Optionally, update selected index if needed!
    blocksManager.setHeroSlideEditing({ selectedSlideIndex: toIdx > fromIdx ? toIdx - 1 : toIdx, selectedSlide: newSlides[toIdx > fromIdx ? toIdx - 1 : toIdx] });
  }

  return (
    <div
      className="
        absolute bottom-0 left-0 right-0 z-40
        backdrop-blur
        flex-col items-center justify-center
        p-4 gap-3
        shadow-2xl
        overflow-x-auto
       "
      style={{
        minHeight: 200,
        background: `
          radial-gradient(
            circle,
            rgba(1,11,1, 0.95) 0%,
            rgba(1,11,1, 0.85) 140%
          )
        `
      }}
    >
      {/* Top row: Total slides text */}
      <div className="text-xs text-gray-300 font-medium pb-2">
        Total Slides: {heroSliderData.slides.length}
      </div>
      <div className="flex-1 flex items-center gap-4 w-max">
          {heroSliderData.slides.map((slide, idx) => (
            <Fragment key={idx}>
              {/* DropZone before each slide */}
              <SlideDropZone dropIndex={idx} onDrop={reorderSlides} />
              <CarouselSlidePreview
                slideImage={slide.slideImage}
                index={idx}
                selected={idx === blocksManager.getHeroSlideEditing().selectedSlideIndex}
                onClick={() => {
                  blocksManager.setHeroSlideEditing({
                    selectedSlideIndex: idx,
                    selectedSlide: slide,
                  });
                  pageBuilderStore.setActivePanel("block-properties");
                }}
              />
            </Fragment>
          ))}
          {/* Final DropZone at the end */}
          <SlideDropZone dropIndex={heroSliderData.slides.length} onDrop={reorderSlides} />
          {/* Add Button */}
          <button
            type="button"
            className={`
              flex items-center justify-center
              rounded-md border-[2px] border-gray-300
              bg-white/30
              shadow-inner
              transition-all duration-200
              hover:shadow-xl
              hover:border-white hover:bg-white/40 hover:border-[2px]
              focus:outline-none
              relative group
              transform-gpu
            `}
            style={{
              minWidth: 140,
              width: 140,
              height: 105
            }}
            onClick={() => 
              contentManager.setHeroSliderConfig(blockId, {
                slides: [
                  ...heroSliderData.slides,
                  getDefaultHeroSlide(heroSliderData.slides.length + 1)
                ]
              })
            }
            aria-label="Add Slide"
          >
            <div
              className={`
                flex items-center justify-center
                w-12 h-12 rounded-full
                bg-white/10
                shadow
                group-hover:shadow-lg
                transform-gpu
                transition-all
                border-transparent
                backdrop-blur-sm
              `}
            >
              <Plus size={32} strokeWidth={3} color={"white"} strokeLinecap="square" />
            </div>
          </button>
      </div>
    </div>
  );
});
